import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useWaitingVariantTracking } from '../useWaitingVariantTracking';

type Phase = 'idle' | 'playing' | 'finished';

type Target = {
	cell: number;
	kind: 'factura' | 'multa';
};

const GRID_CELLS = 9;
const ROUND_SECONDS = 30;
const MOVE_INTERVAL_MS = 900;
const TRAP_CHANCE = 0.25;

const randomTarget = (previousCell?: number): Target => {
	let cell = Math.floor(Math.random() * GRID_CELLS);
	if (cell === previousCell) cell = (cell + 1) % GRID_CELLS;
	return {
		cell,
		kind: Math.random() < TRAP_CHANCE ? 'multa' : 'factura',
	};
};

const getScoreMessage = (score: number): string => {
	if (score >= 20) return '🏆 ¡Reflejos de auditor! Nada se te escapa.';
	if (score >= 12) return '🎯 ¡Muy bien! Así de rápido deberían llegar las devoluciones.';
	if (score >= 6) return '💪 ¡Buen ojo! Con práctica cazas hasta el saldo a favor.';
	return '😅 Las facturas son escurridizas — como las fechas límite.';
};

// Minijuego de reacción: toca las facturas antes de que se muevan y esquiva
// las multas. Ronda de 30 segundos con marcador.
const CazaFacturas = () => {
	const { trackInteraction } = useWaitingVariantTracking();
	const [phase, setPhase] = useState<Phase>('idle');
	const [secondsLeft, setSecondsLeft] = useState(ROUND_SECONDS);
	const [score, setScore] = useState(0);
	const [target, setTarget] = useState<Target | null>(null);

	useEffect(() => {
		if (phase !== 'playing') return;
		const countdown = setInterval(() => {
			setSecondsLeft((prev) => Math.max(0, prev - 1));
		}, 1000);
		const mover = setInterval(() => {
			setTarget((prev) => randomTarget(prev?.cell));
		}, MOVE_INTERVAL_MS);
		return () => {
			clearInterval(countdown);
			clearInterval(mover);
		};
	}, [phase]);

	useEffect(() => {
		if (phase === 'playing' && secondsLeft === 0) {
			setPhase('finished');
			setTarget(null);
			trackInteraction('caza_facturas_finished', { score });
		}
	}, [phase, secondsLeft, score, trackInteraction]);

	const startGame = () => {
		setScore(0);
		setSecondsLeft(ROUND_SECONDS);
		setTarget(randomTarget());
		setPhase('playing');
	};

	const handlePlayAgain = () => {
		trackInteraction('caza_facturas_replayed');
		startGame();
	};

	const handleCellTap = (cell: number) => {
		if (phase !== 'playing' || !target || cell !== target.cell) return;
		if (target.kind === 'factura') {
			setScore((prev) => prev + 1);
		} else {
			setScore((prev) => Math.max(0, prev - 1));
		}
		setTarget(randomTarget(cell));
	};

	if (phase === 'idle') {
		return (
			<div className='flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl bg-h-primary-95 px-5 py-6 text-center'>
				<span className='text-4xl'>🧾</span>
				<h3 className='text-base font-semibold text-h-text-heading'>
					Caza facturas
				</h3>
				<p className='text-sm text-h-neutral-50'>
					Toca las facturas 🧾 antes de que se escondan y esquiva las multas
					⚠️. Tienes {ROUND_SECONDS} segundos.
				</p>
				<Button
					onClick={startGame}
					className='bg-h-primary-50 mt-1 h-11 rounded-xl px-6 font-semibold text-white'
				>
					Empezar
				</Button>
			</div>
		);
	}

	if (phase === 'finished') {
		return (
			<div className='flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl bg-h-primary-95 px-5 py-6 text-center'>
				<span className='text-4xl'>🧾</span>
				<h3 className='text-base font-semibold text-h-text-heading'>
					Cazaste {score} {score === 1 ? 'factura' : 'facturas'}
				</h3>
				<p className='text-sm text-h-neutral-50'>{getScoreMessage(score)}</p>
				<Button
					onClick={handlePlayAgain}
					className='bg-h-primary-50 mt-1 h-11 rounded-xl px-6 font-semibold text-white'
				>
					Jugar otra vez
				</Button>
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-3 rounded-xl bg-h-primary-95 px-4 py-5'>
			<div className='flex items-center justify-between'>
				<p className='text-[11px] font-bold uppercase tracking-[0.08em] text-h-primary-40'>
					🧾 Caza facturas
				</p>
				<p className='text-xs text-h-neutral-50'>
					{score} pts · {secondsLeft}s
				</p>
			</div>
			<p className='text-sm text-h-neutral-50'>
				🧾 suma · ⚠️ resta. ¡Rápido!
			</p>
			<div className='grid grid-cols-3 gap-2'>
				{Array.from({ length: GRID_CELLS }, (_, cell) => {
					const isTarget = target?.cell === cell;
					return (
						<button
							key={cell}
							type='button'
							onClick={() => handleCellTap(cell)}
							aria-label={
								isTarget
									? target.kind === 'factura'
										? 'Factura'
										: 'Multa'
									: 'Casilla vacía'
							}
							className={`flex aspect-square items-center justify-center rounded-lg border transition-colors duration-150 ${
								isTarget
									? 'border-h-primary-70 bg-white'
									: 'border-transparent bg-h-primary-90'
							}`}
						>
							{isTarget && (
								<span className='text-2xl md:text-3xl'>
									{target.kind === 'factura' ? '🧾' : '⚠️'}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

export default CazaFacturas;
